import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormField } from '@ipfabric/ui-components/FormField/FormField';
import { FormSwitch } from '@ipfabric/ui-components/FormSwitch/FormSwitch';
import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import { displayTokenToLabelFilter } from '@ipfabric/utils';
import { isComputedReservedLabelName } from '@ipfabric/utils/labels';
import { useState } from 'react';
import { type SubmitHandler, useWatch } from 'react-hook-form';
import { LabelPicker } from '../../shared/LabelPicker/LabelPicker';
import { ReservedLabelOverrideWarning } from '../../shared/ReservedLabelOverrideWarning';
import { INHERIT_LABEL, INHERIT_TOOLTIP } from '../../shared/inheritSwitchText';
import { OlderSnapshotAssignmentNotice } from '../OlderSnapshotAssignmentNotice';
import { ReflectToRulesFormSwitch } from '../ReflectToRulesFormSwitch';
import { useIsLatestSnapshot } from '../useIsLatestSnapshot';
import { SelectedLabelsList } from './SelectedLabelsList/SelectedLabelsList';
import { TargetField } from './TargetField/TargetField';
import {
  type AssignLabelsFormSchemaValues,
  assignLabelsFormDefaultValues,
  assignLabelsFormSchema,
} from './assignLabelsFormSchema';
import { useAssignLabelsSubmit } from './useAssignLabelsSubmit';

type AssignLabelsFormProps = {
  onClose: () => void;
  onSaved: () => void;
  snapshotId: string;
};

const getAddSelectedLabelsLabel = (count: number) =>
  count > 0 ? `Add ${count} selected labels` : 'Add selected labels';

export const AssignLabelsForm = ({
  onClose,
  onSaved,
  snapshotId,
}: AssignLabelsFormProps) => {
  const [view, setView] = useState<'main' | 'labels'>('main');
  const [pickerDraftLabels, setPickerDraftLabels] = useState<string[]>([]);
  const submitAssignLabels = useAssignLabelsSubmit({ onSaved, snapshotId });
  const isLatestSnapshot = useIsLatestSnapshot(snapshotId);

  const form = useCustomForm<
    AssignLabelsFormSchemaValues,
    unknown,
    AssignLabelsFormSchemaValues
  >({
    defaultValues: {
      ...assignLabelsFormDefaultValues,
      reflectToRules: isLatestSnapshot,
    },
    resolver: zodResolver(assignLabelsFormSchema),
  });

  const { control, formState, handleSubmit, setValue } = form;
  const { errors, isSubmitting } = formState;
  const targets = useWatch({ control, name: 'targets' }) ?? [];
  const labels = useWatch({ control, name: 'labels' }) ?? [];

  const onSubmit: SubmitHandler<AssignLabelsFormSchemaValues> = async (
    data,
  ) => {
    await submitAssignLabels(data);
  };

  const removeLabel = (label: string) => {
    setValue(
      'labels',
      labels.filter((item) => item !== label),
      { shouldDirty: true, shouldValidate: true },
    );
  };

  const openLabelPicker = () => {
    setPickerDraftLabels(labels);
    setView('labels');
  };

  const applyPickerDraft = () => {
    setValue('labels', pickerDraftLabels, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setView('main');
  };

  return (
    <SidePanel
      closeOnBackdropClick={false}
      footer={({ closeModal }) =>
        view === 'labels' ? (
          <div className='flex gap-xs'>
            <Button
              className='flex-1'
              dataTestId='assign-labels-label-picker-add'
              onClick={applyPickerDraft}
              type='button'
              variant='primary'
            >
              {getAddSelectedLabelsLabel(pickerDraftLabels.length)}
            </Button>
            <Button
              className='flex-1'
              dataTestId='assign-labels-label-picker-back'
              onClick={() => setView('main')}
              type='button'
              variant='outline'
            >
              Back
            </Button>
          </div>
        ) : (
          <div className='flex gap-xs'>
            <Button
              className='flex-1'
              dataTestId='assign-labels-submit'
              icon='fa-tags'
              loading={isSubmitting}
              onClick={() => void handleSubmit(onSubmit)()}
              type='button'
              variant='primary'
            >
              Assign labels
            </Button>
            <Button className='flex-1' onClick={closeModal} variant='outline'>
              Close
            </Button>
          </div>
        )
      }
      onClose={onClose}
      title={view === 'labels' ? 'Label selection' : 'Assign labels'}
    >
      {view === 'main' ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void handleSubmit(onSubmit)(event);
          }}
        >
          <Card className='flex flex-col gap p-sm'>
            {!isLatestSnapshot ? <OlderSnapshotAssignmentNotice /> : null}
            <TargetField
              error={errors.targets?.message}
              onChange={(nextTargets) =>
                setValue('targets', nextTargets, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
              value={targets}
            />
            <FormField
              description={
                <span className='inline-flex flex-wrap items-center gap-3xs'>
                  Use
                  <Icon glyph='fa-plus-circle' size='sm' />
                  Add label button to assign label(s).
                </span>
              }
              error={errors.labels?.message}
              label='Labels'
              link={
                <Button
                  dataTestId='assign-labels-add-label-link'
                  icon='fa-plus-circle'
                  iconPosition='start'
                  onClick={openLabelPicker}
                  type='button'
                  variant='plain'
                >
                  Add label
                </Button>
              }
            >
              {labels.length > 0 ? (
                <SelectedLabelsList labels={labels} onRemove={removeLabel} />
              ) : null}
            </FormField>
            <ReservedLabelOverrideWarning
              names={labels
                .map((token) => displayTokenToLabelFilter(token).name)
                .filter(isComputedReservedLabelName)}
            />
            <div data-testid='assign-labels-inherit-switch'>
              <FormSwitch<AssignLabelsFormSchemaValues>
                control={control}
                label={INHERIT_LABEL}
                labelTooltip={INHERIT_TOOLTIP}
                name='inherit'
              />
            </div>
            <ReflectToRulesFormSwitch control={control} />
          </Card>
        </form>
      ) : (
        <LabelPicker
          onChange={setPickerDraftLabels}
          selectedLabels={pickerDraftLabels}
        />
      )}
    </SidePanel>
  );
};
